import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { getClientHasFeaturePermission } from '../../../auth/login/store';
import { SidebarMenu, SidebarMenuOption } from '../../../shared/sidebar-menu/SidebarMenu';
import { fetchAllDropdownListsStarted } from '../../dropdown-lists/store';
import { fetchOpinionSubCounterpartyTypesStarted, fetchOpinionSubCounterpartyTypesSuccessful } from '../../sub-counterparty-types/store';
import { ClientFeaturePermission } from '../../users/store';
import { CompanyEntity, CompanyEntitySection, fetchMyEntityGroupsStarted, selectCompanyEntitySection } from '../store';
import { CompanyEntityBranchesSection } from './CompanyEntityBranches';
import { CompanyEntityContactSection } from './CompanyEntityContact';
import { CompanyEntityDetailsSection } from './CompanyEntityDetails';
import { CompanyEntityDoraSection } from './CompanyEntityDora';
import { CompanyEntityGeneralSection } from './CompanyEntityGeneral';
import styles from './EntityType.module.scss';

interface CompanyEntityFormProps {
    entity: CompanyEntity;
    selectedSection: CompanyEntitySection
}

export const CompanyEntityForm: React.FC<CompanyEntityFormProps> = ({ entity, selectedSection }) => {
    useFetchStarted([fetchMyEntityGroupsStarted(), fetchAllDropdownListsStarted()]);
    const dispatch = useAppDispatch();

    const { content } = entity;
    const opinionCounterpartyType = content.opinionCounterpartyType?.value;
    const opinionJurisdiction = content.opinionJurisdiction || null;

    const selectSection = useCallback((sectionId: string) => dispatch(selectCompanyEntitySection(sectionId as CompanyEntitySection)), [dispatch]);

    const hasDoraClientPermission = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.DORA_MODULE]));

    const sections: SidebarMenuOption[] = useMemo(() => {
        let entitySections = [
            { id: CompanyEntitySection.GENERAL, label: CompanyEntitySection.GENERAL, element: <CompanyEntityGeneralSection entity={entity} /> },
            { id: CompanyEntitySection.CONTACT, label: CompanyEntitySection.CONTACT, element: <CompanyEntityContactSection entity={entity} /> },
            { id: CompanyEntitySection.BRANCHES, label: CompanyEntitySection.BRANCHES, element: <CompanyEntityBranchesSection entity={entity} /> },
            { id: CompanyEntitySection.DETAILS, label: CompanyEntitySection.DETAILS, element: <CompanyEntityDetailsSection entity={entity} /> }
        ];
        if (hasDoraClientPermission) {
            entitySections.push({ id: CompanyEntitySection.DORA, label: CompanyEntitySection.DORA, element: <CompanyEntityDoraSection entity={entity} /> });
        }
        return entitySections;
    }, [entity, hasDoraClientPermission]);

    useEffect(() => {
        if (!isNull(opinionJurisdiction) && !isNull(opinionCounterpartyType)) {
            dispatch(fetchOpinionSubCounterpartyTypesStarted(opinionJurisdiction, opinionCounterpartyType));
        }
        if (isNull(opinionJurisdiction) || isNull(opinionCounterpartyType)) {
            dispatch(fetchOpinionSubCounterpartyTypesSuccessful([]));
        }
    }, [opinionJurisdiction, opinionCounterpartyType, dispatch]);

    return (
        <div className={styles.companyEntityContentWrapper} data-testid='create-entity-content-wrapper'>
            <SidebarMenu
                sections={sections}
                selected={selectedSection}
                menuDisabled={false}
                openSection={selectSection}
                testId='company entity'
                sidebarWidth={110}
            />
        </div>
    );
};
